import React, { useEffect, useMemo, useState } from "react";
import { Plus, KanbanSquare, List, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "./_PageShell";
import KanbanBoard from "@/components/crm/KanbanBoard";
import DealForm from "@/components/crm/DealForm";
import { fmtCurrency, fmtDate } from "@/lib/format";
import { toast } from "sonner";

export default function Deals() {
  const { workspace } = useAuth();
  const dealLabel = workspace?.labels?.deal || "Deal";

  const [pipelines, setPipelines] = useState([]);
  const [activePipelineId, setActivePipelineId] = useState("");
  const [deals, setDeals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [view, setView] = useState("kanban"); // 'kanban' | 'list'
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const activePipeline = useMemo(
    () => pipelines.find((p) => p.id === activePipelineId) || pipelines[0],
    [pipelines, activePipelineId]
  );

  const loadEverything = async () => {
    setLoading(true);
    try {
      const [pipesRes, contactsRes, companiesRes] = await Promise.all([
        api.get("/crm/pipelines"),
        api.get("/crm/contacts"),
        api.get("/crm/companies"),
      ]);
      setPipelines(pipesRes.data);
      setContacts(contactsRes.data);
      setCompanies(companiesRes.data);
      const firstId = pipesRes.data[0]?.id || "";
      setActivePipelineId((cur) => cur || firstId);
      const pid = activePipelineId || firstId;
      if (pid) {
        const { data } = await api.get("/crm/deals", { params: { pipeline_id: pid } });
        setDeals(data);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadDeals = async (pid) => {
    if (!pid) return;
    try {
      const { data } = await api.get("/crm/deals", { params: { pipeline_id: pid } });
      setDeals(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    loadEverything();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activePipelineId) loadDeals(activePipelineId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePipelineId]);

  const openNew = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const onCardClick = (deal) => {
    setEditing(deal);
    setFormOpen(true);
  };

  const onMove = async (deal, newStageId) => {
    // Optimistic update
    setDeals((curr) =>
      curr.map((d) => (d.id === deal.id ? { ...d, stage_id: newStageId } : d))
    );
    try {
      await api.post(`/crm/deals/${deal.id}/move`, {
        stage_id: newStageId,
        position: 0,
      });
    } catch (e) {
      toast.error(apiError(e));
      loadDeals(activePipelineId);
    }
  };

  const totalValue = deals
    .filter((d) => d.status === "open")
    .reduce((s, d) => s + Number(d.value || 0), 0);

  return (
    <>
      <PageHeader
        eyebrow={`CRM · ${dealLabel}s`}
        title={`${dealLabel}s`}
        description={
          activePipeline
            ? `Pipeline · ${activePipeline.name} · ${deals.length} ${deals.length === 1 ? "deal" : "deals"} · ${fmtCurrency(totalValue, deals[0]?.currency || "INR")} open`
            : "Loading…"
        }
        actions={
          <>
            {pipelines.length > 1 && (
              <select
                value={activePipelineId}
                onChange={(e) => setActivePipelineId(e.target.value)}
                className="editorial-input !py-2 !text-sm !w-auto min-w-[160px]"
                data-testid="pipeline-switcher"
              >
                {pipelines.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            )}

            <div className="inline-flex rounded-full bg-[#F5F4F0] p-1">
              <button
                onClick={() => setView("kanban")}
                data-testid="view-kanban"
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono-tech tracking-[0.12em] uppercase transition-colors ${
                  view === "kanban" ? "bg-[#1A1A1A] text-[#FDFCFB]" : "text-[#6B6A68]"
                }`}
              >
                <KanbanSquare className="w-3.5 h-3.5" /> Kanban
              </button>
              <button
                onClick={() => setView("list")}
                data-testid="view-list"
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono-tech tracking-[0.12em] uppercase transition-colors ${
                  view === "list" ? "bg-[#1A1A1A] text-[#FDFCFB]" : "text-[#6B6A68]"
                }`}
              >
                <List className="w-3.5 h-3.5" /> List
              </button>
            </div>

            <button
              onClick={openNew}
              data-testid="deal-new-button"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1A1A1A] text-[#FDFCFB] text-sm hover:bg-[#333]"
            >
              <Plus className="w-4 h-4" />
              New {dealLabel.toLowerCase()}
            </button>
          </>
        }
      />

      {loading || !activePipeline ? (
        <div className="px-6 md:px-10 py-12 flex items-center gap-2 text-sm text-[#6B6A68]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : deals.length === 0 ? (
        <div className="px-6 md:px-10 py-12">
          <div className="text-center py-16 px-6 rounded-2xl bg-[#F5F4F0]">
            <div className="caption mb-3">Empty pipeline</div>
            <p className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A] tracking-tight">
              No {dealLabel.toLowerCase()}s yet — create your first.
            </p>
            <button
              onClick={openNew}
              data-testid="deal-empty-cta"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1A1A1A] text-[#FDFCFB] text-sm"
            >
              <Plus className="w-4 h-4" /> New {dealLabel.toLowerCase()}
            </button>
          </div>
        </div>
      ) : view === "kanban" ? (
        <KanbanBoard
          pipeline={activePipeline}
          deals={deals}
          contacts={contacts}
          companies={companies}
          onCardClick={onCardClick}
          onMove={onMove}
        />
      ) : (
        <DealListView
          deals={deals}
          pipeline={activePipeline}
          contacts={contacts}
          companies={companies}
          onRowClick={onCardClick}
        />
      )}

      <DealForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        deal={editing}
        pipelines={pipelines}
        defaultPipelineId={activePipelineId}
        contacts={contacts}
        companies={companies}
        onSaved={() => loadDeals(activePipelineId)}
      />
    </>
  );
}

function DealListView({ deals, pipeline, contacts, companies, onRowClick }) {
  const contactsById = useMemo(
    () => Object.fromEntries(contacts.map((c) => [c.id, c])),
    [contacts]
  );
  const companiesById = useMemo(
    () => Object.fromEntries(companies.map((c) => [c.id, c])),
    [companies]
  );
  const stagesById = useMemo(
    () => Object.fromEntries(pipeline.stages.map((s) => [s.id, s])),
    [pipeline]
  );

  return (
    <div className="px-6 md:px-10 py-6">
      <div className="rounded-2xl border border-[#E8E6E1] bg-[#FDFCFB] overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#E8E6E1] caption !text-[10px] bg-[#F5F4F0]">
          <div className="col-span-4">Title</div>
          <div className="col-span-2">Stage</div>
          <div className="col-span-2 text-right">Value</div>
          <div className="col-span-2">Contact</div>
          <div className="col-span-2">Close</div>
        </div>
        <ul className="divide-y divide-[#E8E6E1]" data-testid="deal-list">
          {deals.map((d) => {
            const stage = stagesById[d.stage_id];
            const contact = contactsById[d.contact_id];
            const company = companiesById[d.company_id];
            return (
              <li key={d.id}>
                <button
                  type="button"
                  onClick={() => onRowClick(d)}
                  data-testid={`deal-row-${d.id}`}
                  className="w-full text-left px-5 py-4 hover:bg-[#F5F4F0] transition-colors grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 items-center"
                >
                  <div className="md:col-span-4">
                    <div className="text-[15px] text-[#1A1A1A] font-medium tracking-tight truncate">
                      {d.title}
                    </div>
                    {company?.name && (
                      <div className="text-xs text-[#6B6A68] mt-0.5 truncate">
                        {company.name}
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-2 text-xs text-[#1A1A1A] font-mono-tech tracking-[0.1em] uppercase">
                    {stage?.name || "—"}
                  </div>
                  <div className="md:col-span-2 text-sm text-[#1A1A1A] md:text-right">
                    {fmtCurrency(d.value, d.currency)}
                  </div>
                  <div className="md:col-span-2 text-xs text-[#6B6A68] truncate">
                    {contact?.name || "—"}
                  </div>
                  <div className="md:col-span-2 text-xs text-[#6B6A68]">
                    {fmtDate(d.expected_close)}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
